import { useTranslation } from "react-i18next";
import { Icons } from "@/components/Icons";
import { useBranch } from "@/store/branch";
import { localizeValue } from "@/lib/fulfillment";

type Variant = "banner" | "inline";

/**
 * A merchant-authored, multilingual note attached to the menu link — typically a
 * pricing disclaimer such as "prices include tax but not service charge".
 *
 * Renders nothing when the note is absent or empty in every language, so it is
 * safe to mount unconditionally. Line breaks the merchant typed are preserved
 * (`white-space: pre-line`); the text is plain — no markup is interpreted.
 */
export function StorefrontNote({ variant = "banner" }: { variant?: Variant }) {
  const { i18n } = useTranslation();
  const { branchConfig } = useBranch();

  const text = localizeValue(branchConfig?.storefrontNote, i18n.language || "en");
  if (!text.trim()) return null;

  if (variant === "inline") {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 6,
          marginTop: 10,
          fontSize: 12,
          lineHeight: 1.5,
          color: "var(--ink-3)",
          whiteSpace: "pre-line",
        }}
      >
        <span style={{ flexShrink: 0, marginTop: 2, display: "inline-flex" }}>
          <Icons.info size={12} />
        </span>
        <span style={{ minWidth: 0 }}>{text}</span>
      </div>
    );
  }

  return (
    <div
      style={{
        background: "var(--surface-2)",
        borderBottom: "1px solid var(--border)",
        padding: "12px 16px",
        display: "flex",
        alignItems: "flex-start",
        gap: 10,
      }}
    >
      <div
        style={{
          width: 28,
          height: 28,
          borderRadius: 999,
          background: "var(--border)",
          display: "grid",
          placeItems: "center",
          color: "var(--ink-2)",
          flexShrink: 0,
        }}
      >
        <Icons.info size={14} />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          fontSize: 13,
          lineHeight: 1.5,
          color: "var(--ink-2)",
          whiteSpace: "pre-line",
        }}
      >
        {text}
      </div>
    </div>
  );
}
